export interface MusicBrainzCoverResult {
  coverUrl: string;
  releaseTitle: string;
  artist: string;
  releaseId: string;
}

interface MusicBrainzRecording {
  title?: string;
  'artist-credit'?: Array<{ name?: string; artist?: { name?: string } }>;
  releases?: Array<{ id?: string; title?: string }>
}

interface MusicBrainzSearchResponse {
  recordings?: MusicBrainzRecording[];
}

/** Find a recording and its front cover through MusicBrainz and Cover Art Archive. */
export function parseTrackFilename(filename: string): { title: string; artist?: string } {
  const withoutExtension = filename.replace(/\.[^/.]+$/, '').replace(/[._]+/g, ' ').trim();
  const parts = withoutExtension.split(/\s[-–—]\s/);
  if (parts.length >= 2) return { artist: parts[0].trim(), title: parts.slice(1).join(' - ').trim() };
  return { title: withoutExtension };
}

export async function findMusicBrainzRecording(title: string, artist?: string, signal?: AbortSignal): Promise<MusicBrainzCoverResult | null> {
  const query = artist
    ? `recording:"${title}" AND artist:"${artist}"`
    : `recording:"${title}"`;
  const response = await fetch(`https://musicbrainz.org/ws/2/recording/?query=${encodeURIComponent(query)}&fmt=json&limit=5`, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`MusicBrainz lookup failed (${response.status})`);
  const data = await response.json() as MusicBrainzSearchResponse;
  const recording = data.recordings?.find((item) => item.title && item.releases?.some((release) => release.id));
  const release = recording?.releases?.find((item) => item.id);
  if (!recording?.title || !release?.id) return null;
  return {
    coverUrl: `https://coverartarchive.org/release/${encodeURIComponent(release.id)}/front-500`,
    releaseId: release.id,
    releaseTitle: recording.title,
    artist: recording['artist-credit']?.[0]?.name ?? recording['artist-credit']?.[0]?.artist?.name ?? artist ?? 'Unknown artist',
  };
}

export async function findMusicBrainzCover(artist: string, title: string, signal?: AbortSignal): Promise<MusicBrainzCoverResult | null> {
  const query = encodeURIComponent(`recording:"${title}" AND artist:"${artist}"`);
  const response = await fetch(`https://musicbrainz.org/ws/2/recording/?query=${query}&fmt=json&limit=5`, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`MusicBrainz lookup failed (${response.status})`);
  const data = await response.json() as MusicBrainzSearchResponse;
  const recording = data.recordings?.find((item) => item.releases?.some((release) => release.id));
  const release = recording?.releases?.find((item) => item.id);
  if (!release?.id) return null;
  const coverUrl = `https://coverartarchive.org/release/${encodeURIComponent(release.id)}/front-500`;
  return {
    coverUrl,
    releaseId: release.id,
    releaseTitle: release.title ?? title,
    artist: recording?.['artist-credit']?.[0]?.name ?? recording?.['artist-credit']?.[0]?.artist?.name ?? artist,
  };
}
